/**
 * PageTimingProvider — measures a page load and assembles a per-component
 * drill-down, emitted once when the page settles:
 *   - browser console: a collapsed group + console.table waterfall (slow block obvious)
 *   - service logs: one structured WebLogger record → /console/client-logs → tracingLogs
 *
 * Page-level `pageLoadMs` runs from mount until no query is still BLOCKING the
 * screen (see `BLOCKING_FETCHES`); `useComponentTiming` calls from blocks add
 * the breakdown.
 *
 * Design: docs/FeatureDesigns/page-load-perf-drilldown.md
 */

import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useIsFetching, useQueryClient, type QueryFilters } from '@tanstack/react-query';
import { WebLogger, WebComponentCode, getWebTracingManager, LogLevel, setCurrentPageLoadId } from '@shared-ui/logging';
import { PageTimingContext } from './pageTimingContext';

const logger = new WebLogger(WebComponentCode.WEB_PERF, 'PagePerf');

/**
 * What still counts as "the page isn't ready": a query that is in flight and has
 * no data yet, so something on screen is still a skeleton.
 *
 * A refetch of a query that ALREADY has data is deliberately not counted. The
 * teacher's assignment detail page polls itself every 4s while a PDF import is
 * building (`refetchInterval` in QuestionSetDetail), and the layout polls unread
 * counts; against a plain global fetch count those never let the page be called
 * settled, so `pageLoadMs` kept running long after the teacher was looking at a
 * finished screen — dev showed loads recorded at 12s and one at 69s whose total
 * server time was under half a second. Those are the poll, not the wait.
 */
const BLOCKING_FETCHES: QueryFilters = { predicate: q => q.state.data === undefined };

/**
 * Longest a page load may be measured for. Past this the number has stopped
 * describing anything a person waited through, and one stuck query would
 * otherwise contribute an outlier that drags every percentile it lands in.
 * Capped samples are emitted with `capped: true` and counted separately in the
 * report rather than dropped — silently discarding the worst loads would flatter
 * the numbers. Shorter than `useActionTiming`'s 60s cap: nobody waits a minute
 * for a page and stays.
 */
const PAGE_SETTLE_CAP_MS = 30_000;

// Module-level dedupe. One navigation can trigger several emit attempts — React
// StrictMode (dev) double-mounts the provider, and background refetches can bounce
// the global fetch count to 0 more than once. All share the same navKey
// (react-router location.key), so we emit exactly once per navigation.
let lastEmittedNavKey: string | undefined;

interface ComponentTiming { name: string; ms: number; fromCache: boolean; }

function genId(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* fall through */ }
  return `pl-${Math.round(performance.now())}-${Math.floor(Math.random() * 1e6)}`;
}

function emit(
  page: string, pageLoadId: string, pageLoadMs: number, fromCache: boolean,
  components: ComponentTiming[], capped: boolean,
): void {
  // Recorded for perf analysis, but never at warn level — a slow page load must not
  // surface an on-screen popup to the student.
  logger.info2(`${page} page perf`, { page, pageLoadId, pageLoadMs, fromCache, components, capped });

  // Drill-down waterfall in the browser console — gated on the WEB_PERF level so
  // it respects the same toggle as the rest of the perf logging.
  try {
    const enabled = getWebTracingManager().isLevelEnabled(LogLevel.INFO2, WebComponentCode.WEB_PERF);
    if (enabled && components.length && typeof console !== 'undefined' && console.table) {
      const suffix = capped ? ` (capped at ${PAGE_SETTLE_CAP_MS}ms — floor, not the real value)` : '';
      console.groupCollapsed(`[perf] ${page} — ${pageLoadMs}ms (${components.length} blocks)${suffix}`);
      console.table(components.map(c => ({ block: c.name, ms: c.ms, fromCache: c.fromCache })));
      console.groupEnd();
    }
  } catch { /* console/table unavailable — ignore */ }
}

export function PageTimingProvider({ navKey, page, children }: { navKey: string; page: string; children: ReactNode }) {
  const qc = useQueryClient();
  const isFetching = useIsFetching(BLOCKING_FETCHES);

  const startRef = useRef(performance.now());
  const pageLoadIdRef = useRef(genId());
  const componentsRef = useRef<Map<string, ComponentTiming>>(new Map());
  const emittedRef = useRef(false);
  const sawFetchingRef = useRef(false);
  const navKeyRef = useRef<string | undefined>(undefined);

  // A new navigation → reset for a fresh measurement. The provider is hosted in
  // the layout and persists across child route changes, so we reset on navKey
  // change rather than relying on remount. Runs during render (derived-state
  // pattern) so children see the fresh pageLoadId immediately.
  if (navKeyRef.current !== navKey) {
    navKeyRef.current = navKey;
    startRef.current = performance.now();
    pageLoadIdRef.current = genId();
    componentsRef.current = new Map();
    emittedRef.current = false;
    sawFetchingRef.current = false;
    // Publish for the API client to stamp on requests made during this load.
    setCurrentPageLoadId(pageLoadIdRef.current);
  }

  if (isFetching > 0) sawFetchingRef.current = true;

  // Stable identity so useComponentTiming effects don't churn. First report per
  // block wins (its initial settle), later refetches are ignored.
  const report = useCallback((name: string, ms: number, fromCache: boolean) => {
    if (!componentsRef.current.has(name)) componentsRef.current.set(name, { name, ms, fromCache });
  }, []);

  /** Emit this navigation's sample, once. Both endings — settled, and the cap
   *  expiring — land here so the dedupe and the reset live in one place. */
  const finish = useCallback((capped: boolean) => {
    if (emittedRef.current) return;
    emittedRef.current = true;
    if (lastEmittedNavKey === navKey) return; // dedupe: one emit per navigation
    lastEmittedNavKey = navKey;
    const pageLoadMs = Math.round(performance.now() - startRef.current);
    const components = [...componentsRef.current.values()].sort((a, b) => b.ms - a.ms);
    emit(page, pageLoadIdRef.current, pageLoadMs, !sawFetchingRef.current, components, capped);
  }, [navKey, page]);

  useEffect(() => {
    if (emittedRef.current || isFetching > 0) return;
    // isFetching is 0 this render; confirm on the next macrotask that nothing
    // (re)started. Child useComponentTiming effects run before this parent effect,
    // so their reports are already collected by the time we emit.
    const id = setTimeout(() => {
      if (emittedRef.current || qc.isFetching(BLOCKING_FETCHES) > 0) return;
      finish(false);
    }, 0);
    return () => clearTimeout(id);
  }, [isFetching, navKey, qc, finish]);

  // The cap. Restarts per navigation; cleared as soon as the page settles
  // normally, so in the ordinary case it never fires.
  useEffect(() => {
    const id = setTimeout(() => finish(true), PAGE_SETTLE_CAP_MS);
    return () => clearTimeout(id);
  }, [navKey, finish]);

  const ctx = useMemo(() => ({ report, active: true, page }), [report, page]);

  return (
    <PageTimingContext.Provider value={ctx}>
      {children}
    </PageTimingContext.Provider>
  );
}
